import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Grid,
    Paper,
    Chip,
    TextField,
    IconButton,
    Tooltip,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import {
    ExpandMore as ExpandMoreIcon,
    ChevronRight as ChevronRightIcon,
    Business as BusinessIcon,
    LocalHospital as LocalHospitalIcon,
    LocationOn as LocationOnIcon,
    Group as GroupIcon,
    MedicalServices as MedicalServicesIcon,
    CalendarToday as CalendarTodayIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Sync as SyncIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface HierarchicalNode {
    id?: string;
    code?: string;
    name?: string;
    description?: string;
    type?: string;
    externalCode?: string;
    hospital?: HierarchicalNode[];
    sites?: HierarchicalNode[];
    operation_units?: HierarchicalNode[];
    operationUnits?: HierarchicalNode[];
    execution_units?: HierarchicalNode[];
    executionUnits?: HierarchicalNode[];
    diaries?: HierarchicalNode[];
    children?: HierarchicalNode[];
    [key: string]: any;
}

const HierarchicalStructure: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [structure, setStructure] = useState<HierarchicalNode[] | null>(null);
    const [expanded, setExpanded] = useState<string[]>([]);
    const [selected, setSelected] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedUnits, setSelectedUnits] = useState<HierarchicalNode[]>([]);
    const [syncing, setSyncing] = useState<boolean>(false);
    const [selectedLevel, setSelectedLevel] = useState<string>('diary');
    const [syncStats, setSyncStats] = useState<{[key: string]: {created: number, updated: number}} | null>(null);
    const [loadingProfile, setLoadingProfile] = useState<boolean>(true);

    // Il nsisCode viene recuperato OBBLIGATORIAMENTE dal profilo attivo - NO DEFAULT HARDCODATO
    const [nsisCode, setNsisCode] = useState<string | null>(null);

    const levelOptions = [
        { value: 'authority', label: 'Authority' },
        { value: 'hospital', label: 'Hospital' },
        { value: 'site', label: 'Site' },
        { value: 'operation_unit', label: 'Operation Unit' },
        { value: 'execution_unit', label: 'Execution Unit' },
        { value: 'diary', label: 'Diary' }
    ];

    // Carica la configurazione del profilo attivo all'avvio
    useEffect(() => {
        const loadProfileConfig = async () => {
            setLoadingProfile(true);
            try {
                console.log('🔄 Loading active RECUP profile configuration...');
                const response = await api.get('/recup-profiles/active');
                console.log('📋 Active profile response:', response.data);

                if (!response.data.data?.nsisCode) {
                    throw new Error('Active profile does not have nsisCode configured');
                }

                const newNsisCode = response.data.data.nsisCode;
                console.log('🎯 Setting NSIS code from active profile:', newNsisCode);
                setNsisCode(newNsisCode);
            } catch (err: any) {
                console.error('❌ CRITICAL: Could not load active profile configuration:', err);
                setError('Impossibile caricare il profilo RECUP attivo. Verificare la configurazione.');
                setNsisCode(null); // NON usare default - forza l'utente a configurare il profilo
            } finally {
                setLoadingProfile(false);
            }
        };
        loadProfileConfig();
    }, []);

    useEffect(() => {
        // Non caricare la struttura gerarchica finché non abbiamo caricato il profilo
        if (loadingProfile) return;
        
        // Evita chiamate multiple durante il cambio profilo
        if (nsisCode) {
            console.log('🏗️ Loading hierarchical structure with NSIS:', nsisCode);
            loadHierarchicalStructure();
        }
    }, [selectedLevel, nsisCode, loadingProfile]);

    const loadHierarchicalStructure = async () => {
        // Evita chiamate concorrenti
        if (loading) {
            console.log('⏸️ Skipping hierarchical structure load - already loading');
            return;
        }
        
        setLoading(true);
        setError(null);
        try {
            console.log('📡 Fetching hierarchical structure...', { nsisCode, level: selectedLevel });
            const response = await api.get('/recup-sync/hierarchical-structure', {
                params: {
                    nsisCode,
                    level: selectedLevel
                }
            });
            // Gestisci la struttura dati di ReCup
            const data = response.data;
            console.log('Response data:', data);
            
            if (data) {
                // Se ha una proprietà authority, usa quella come root
                if (data.authority) {
                    console.log('Setting authority as structure:', data.authority);
                    setStructure([data.authority]);
                } else if (Array.isArray(data)) {
                    console.log('Setting array as structure:', data);
                    setStructure(data);
                } else {
                    console.log('Setting single object as structure:', data);
                    setStructure([data]);
                }
            } else {
                console.log('No data, setting empty structure');
                setStructure([]);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Errore nel caricamento della struttura gerarchica');
            console.error('Error loading hierarchical structure:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
        setExpanded(nodeIds);
    };

    const handleSelect = (event: React.SyntheticEvent, nodeIds: string | string[] | null) => {
        if (typeof nodeIds === 'string') {
            setSelected(nodeIds);
        }
    };

    const getNodeIcon = (node: HierarchicalNode) => {
        // Determina il tipo di nodo basandosi sulla struttura
        if (node.diaries !== undefined && (node.executionUnits || node.execution_units)) {
            return <GroupIcon color="action" />; // Operation Unit
        }
        if (node.diaries !== undefined && !node.executionUnits && !node.execution_units) {
            return <MedicalServicesIcon color="success" />; // Execution Unit
        }
        if (node.externalCode !== undefined || (node.name && node.name.includes('AGENDA'))) {
            return <CalendarTodayIcon color="info" />; // Diary
        }
        if (node.sites !== undefined) {
            return <LocalHospitalIcon color="primary" />; // Hospital
        }
        if (node.operationUnits !== undefined || node.operation_units !== undefined) {
            return <LocationOnIcon color="secondary" />; // Site
        }
        
        // Fallback basato sul type se presente
        switch (node.type) {
            case 'hospital':
            case 'presidio':
                return <LocalHospitalIcon color="primary" />;
            case 'site':
            case 'sede':
                return <LocationOnIcon color="secondary" />;
            case 'operation_unit':
            case 'raggruppamento':
                return <GroupIcon color="action" />;
            case 'execution_unit':
            case 'unita_erogante':
                return <MedicalServicesIcon color="success" />;
            case 'diary':
            case 'agenda':
                return <CalendarTodayIcon color="info" />;
            default:
                return <BusinessIcon />;
        }
    };

    const renderTreeNodes = (nodes: HierarchicalNode[] | null | undefined, parentPath: string = '', visitedIds: Set<string> = new Set()): React.ReactNode => {
        if (!nodes) return null;
        
        // Gestisci sia array che oggetti singoli
        const nodeArray = Array.isArray(nodes) ? nodes : [nodes];
        console.log('Rendering nodes:', nodeArray);
        
        return nodeArray
            .filter(node => {
                // Crea una chiave unica per il nodo basata sul percorso completo
                const nodeKey = `${parentPath}/${node.id || node.code || node.name || ''}`;
                if (visitedIds.has(nodeKey)) {
                    console.log('Skipping visited node:', nodeKey);
                    return false;
                }
                
                if (!searchTerm) return true;
                const searchLower = searchTerm.toLowerCase();
                return (
                    node.description?.toLowerCase().includes(searchLower) ||
                    node.code?.toLowerCase().includes(searchLower) ||
                    node.name?.toLowerCase().includes(searchLower)
                );
            })
            .map((node) => {
                const nodeKeyBase = `${node.id || node.code || node.name || ''}`;
                const nodeId = `${parentPath}/${node.type || 'unknown'}/${nodeKeyBase}`;
                const nodeFullKey = `${parentPath}/${nodeKeyBase}`;
                const isExecutionUnit = node.type === 'execution_unit' || node.type === 'unita_erogante' || 
                                       (node.diaries !== undefined && !node.executionUnits && !node.execution_units);
                
                // Aggiungi questo nodo all'insieme dei visitati usando la chiave completa
                const newVisitedIds = new Set(visitedIds);
                newVisitedIds.add(nodeFullKey);
                
                return (
                    <TreeItem
                        key={nodeId}
                        itemId={nodeId}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5 }}>
                                {getNodeIcon(node)}
                                <Typography sx={{ ml: 1, flexGrow: 1 }}>
                                    {node.description || node.name} 
                                    {(node.code || node.externalCode) && (
                                        <Chip 
                                            label={node.code || `Code: ${node.externalCode}`} 
                                            size="small" 
                                            sx={{ ml: 1 }}
                                            variant="outlined"
                                        />
                                    )}
                                </Typography>
                                {isExecutionUnit && (
                                    <Tooltip title="Seleziona per sincronizzazione">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleUnitSelection(node);
                                            }}
                                            color={selectedUnits.find(u => u.id === node.id) ? "primary" : "default"}
                                        >
                                            <SyncIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        }
                    >
                        {node.hospital && renderTreeNodes(node.hospital, nodeId, newVisitedIds)}
                        {node.sites && renderTreeNodes(node.sites, nodeId, newVisitedIds)}
                        {node.operation_units && renderTreeNodes(node.operation_units, nodeId, newVisitedIds)}
                        {node.operationUnits && renderTreeNodes(node.operationUnits, nodeId, newVisitedIds)}
                        {node.execution_units && renderTreeNodes(node.execution_units, nodeId, newVisitedIds)}
                        {node.executionUnits && renderTreeNodes(node.executionUnits, nodeId, newVisitedIds)}
                        {node.diaries && renderTreeNodes(node.diaries, nodeId, newVisitedIds)}
                        {node.children && renderTreeNodes(node.children, nodeId, newVisitedIds)}
                    </TreeItem>
                );
            });
    };

    const toggleUnitSelection = (unit: HierarchicalNode) => {
        setSelectedUnits(prev => {
            const exists = prev.find(u => u.id === unit.id);
            if (exists) {
                return prev.filter(u => u.id !== unit.id);
            } else {
                return [...prev, unit];
            }
        });
    };

    const syncEntity = async (entityType: string) => {
        setSyncing(true);
        setError(null);

        try {
            let endpoint = '';
            let entityName = '';
            
            switch (entityType) {
                case 'authorities':
                    endpoint = '/recup-sync/sync/authorities';
                    entityName = 'Authorities';
                    break;
                case 'hospitals':
                    endpoint = '/recup-sync/sync/hospitals';
                    entityName = 'Hospitals';
                    break;
                case 'sites':
                    endpoint = '/recup-sync/sync/sites';
                    entityName = 'Sites';
                    break;
                case 'operation-units':
                    endpoint = '/recup-sync/sync/operation-units';
                    entityName = 'Operation Units';
                    break;
                case 'execution-units':
                    endpoint = '/recup-sync/sync/execution-units';
                    entityName = 'Execution Units';
                    break;
                case 'hierarchy':
                    endpoint = '/recup-sync/sync/hierarchy';
                    entityName = 'Complete Hierarchy';
                    break;
                default:
                    throw new Error('Invalid entity type');
            }

            const response = await api.post(endpoint, {}, {
                params: {
                    nsisCode: nsisCode
                }
            });

            if (response.data.success) {
                if (entityType === 'hierarchy') {
                    // Messaggio speciale per sincronizzazione completa
                    const summary = response.data.summary;
                    const message = `Struttura gerarchica completa sincronizzata: ${summary.created || 0} create, ${summary.updated || 0} aggiornate`;
                    alert(message);
                } else {
                    // Messaggio per singola entità
                    const message = `${entityName} sincronizzate: ${response.data.created || 0} create, ${response.data.updated || 0} aggiornate`;
                    alert(message);
                    
                    // Aggiorna le statistiche di sincronizzazione
                    setSyncStats(prev => ({
                        ...prev,
                        [entityType]: {
                            created: response.data.created || 0,
                            updated: response.data.updated || 0
                        }
                    }));
                }
            } else {
                setError(response.data.message || `Errore durante la sincronizzazione di ${entityName}`);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || `Errore durante la sincronizzazione di ${entityType}`);
        } finally {
            setSyncing(false);
        }
    };

    const syncSelectedUnits = async () => {
        if (selectedUnits.length === 0) {
            setError('Seleziona almeno un\'unità erogante da sincronizzare');
            return;
        }

        setSyncing(true);
        setError(null);

        try {
            // Prima sincronizza le unità eroganti
            const response = await api.post('/recup-sync/sync/execution-units', {
                units: selectedUnits
            });

            if (response.data.success) {
                const unitsMessage = `Unità eroganti sincronizzate: ${response.data.created || 0} create, ${response.data.updated || 0} aggiornate`;
                
                // Chiedi se sincronizzare anche le agende
                if (window.confirm(`${unitsMessage}\n\nVuoi sincronizzare anche le agende per le unità eroganti selezionate?`)) {
                    // Sincronizza le agende
                    const executionUnitCodes = selectedUnits.map(unit => unit.code || unit.id);
                    const diariesResponse = await api.post('/recup-sync/sync/diaries', {
                        executionUnitCodes
                    });
                    
                    if (diariesResponse.data.success) {
                        navigate('/execution-units', { 
                            state: { 
                                message: `${unitsMessage}. Agende sincronizzate: ${diariesResponse.data.created || 0} create, ${diariesResponse.data.updated || 0} aggiornate` 
                            }
                        });
                    }
                } else {
                    navigate('/execution-units', { 
                        state: { 
                            message: unitsMessage
                        }
                    });
                }
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Errore durante la sincronizzazione');
        } finally {
            setSyncing(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h5" component="h2">
                                    <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Struttura Gerarchica ReCup
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Chip
                                        label={nsisCode ? `NSIS: ${nsisCode}` : 'NSIS: Non configurato'}
                                        color={nsisCode ? "primary" : "error"}
                                    />
                                    <Chip
                                        label={`Level: ${selectedLevel.toUpperCase()}`}
                                        color="secondary"
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={loadHierarchicalStructure}
                                        disabled={loading || !nsisCode}
                                    >
                                        Ricarica
                                    </Button>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {!nsisCode && !loadingProfile && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    <strong>Profilo RECUP non configurato!</strong>
                                    <br />
                                    Il profilo RECUP attivo non ha un codice NSIS configurato.
                                    Vai alla sezione "Profili RECUP" per configurare il profilo con i parametri corretti.
                                </Alert>
                            )}

                            {loadingProfile && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">
                                        Caricamento configurazione profilo RECUP...
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel id="level-select-label">Livello</InputLabel>
                                    <Select
                                        labelId="level-select-label"
                                        id="level-select"
                                        value={selectedLevel}
                                        label="Livello"
                                        onChange={(e: SelectChangeEvent) => setSelectedLevel(e.target.value)}
                                        disabled={loading}
                                    >
                                        {levelOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    fullWidth
                                    placeholder="Cerca nella struttura..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                    }}
                                />
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<SyncIcon />}
                                        onClick={() => syncEntity('authorities')}
                                        disabled={syncing}
                                        size="small"
                                    >
                                        Sync Authorities
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<SyncIcon />}
                                        onClick={() => syncEntity('hospitals')}
                                        disabled={syncing}
                                        size="small"
                                    >
                                        Sync Hospitals
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<SyncIcon />}
                                        onClick={() => syncEntity('sites')}
                                        disabled={syncing}
                                        size="small"
                                    >
                                        Sync Sites
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<SyncIcon />}
                                        onClick={() => syncEntity('operation-units')}
                                        disabled={syncing}
                                        size="small"
                                    >
                                        Sync Operation Units
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<SyncIcon />}
                                        onClick={() => syncEntity('execution-units')}
                                        disabled={syncing}
                                        size="small"
                                    >
                                        Sync Execution Units
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={<SyncIcon />}
                                        onClick={() => syncEntity('hierarchy')}
                                        disabled={syncing}
                                    >
                                        Sync Complete Hierarchy
                                    </Button>
                                    {selectedUnits.length > 0 && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<SyncIcon />}
                                            onClick={syncSelectedUnits}
                                            disabled={syncing}
                                        >
                                            Sync Selected Units ({selectedUnits.length})
                                        </Button>
                                    )}
                                </Box>
                            </Box>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                                    {error}
                                </Alert>
                            )}

                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : structure ? (
                                <Paper variant="outlined" sx={{ p: 2, maxHeight: '600px', overflow: 'auto' }}>
                                    <SimpleTreeView
                                        slots={{
                                            collapseIcon: ExpandMoreIcon,
                                            expandIcon: ChevronRightIcon
                                        }}
                                        expandedItems={expanded}
                                        selectedItems={selected}
                                        onExpandedItemsChange={handleToggle}
                                        onSelectedItemsChange={handleSelect}
                                    >
                                        {renderTreeNodes(structure)}
                                    </SimpleTreeView>
                                </Paper>
                            ) : (
                                <Alert severity="info">
                                    Nessuna struttura gerarchica disponibile. 
                                    Verifica che il codice NSIS sia configurato correttamente nel sistema ReCup.
                                </Alert>
                            )}

                            {selectedUnits.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Unità selezionate per la sincronizzazione:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {selectedUnits.map(unit => (
                                            <Chip
                                                key={unit.id}
                                                label={`${unit.description || unit.name} (${unit.code})`}
                                                onDelete={() => toggleUnitSelection(unit)}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {syncStats && (
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Statistiche Sincronizzazione
                                </Typography>
                                <Grid container spacing={2}>
                                    {Object.entries(syncStats).map(([entityType, stats]) => (
                                        <Grid item xs={12} sm={6} md={4} key={entityType}>
                                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                                <Typography variant="subtitle2" color="primary">
                                                    {entityType.charAt(0).toUpperCase() + entityType.slice(1).replace('-', ' ')}
                                                </Typography>
                                                <Typography variant="body2">
                                                    Creati: <strong>{stats.created}</strong>
                                                </Typography>
                                                <Typography variant="body2">
                                                    Aggiornati: <strong>{stats.updated}</strong>
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Nota:</strong> La struttura gerarchica viene recuperata dal sistema ReCup utilizzando il codice NSIS configurato.
                            Seleziona le unità eroganti che desideri sincronizzare nel sistema locale.
                            Le agende associate verranno recuperate automaticamente durante la sincronizzazione.
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default HierarchicalStructure;